import { formatMoney } from '../../lib/format'
import { Avatar } from '../../components/Avatar'
import type { AccountPlan } from '../../types/accounts'

// The account at a glance: the best way in as a path, then the numbers that matter — people known,
// decision makers reached, the strongest route, open pursuits, the pipeline — and the flags to act on.

export function AccountGlance({ plan, ownerNames, onOpen }: {
  plan: AccountPlan; ownerNames: Record<string, string>; onOpen: (personId: string) => void
}) {
  const inf = plan.influence
  const { openPursuits, pipeline } = plan
  const people = plan.contacts.filter((c) => c.person.role !== 'colleague')
  const person = new Map(people.map((c) => [c.person.personId, c]))
  const best = inf.routes[0]
  const target = best ? person.get(best.targetPersonId) : null
  const via = best?.viaPersonId ? person.get(best.viaPersonId) : null
  const dmTotal = inf.decisionMakerIds.length
  const dmReached = dmTotal - inf.unreachedIds.length
  const strongest = best ? Math.round(best.strength * 100) : null
  const quiet = people.filter((c) => c.signals.some((s) => s.type === 'dormant'))
  const moved = people.filter((c) => c.signals.some((s) => s.type === 'job-change'))
  const owners = new Set(people.map((c) => c.person.ownerId).filter(Boolean))

  return (
    <section className="glance acct-glance">
      <div className="acct-best">
        <p className="kicker">Best way in</p>
        {best && target ? (
          <>
            <div className="acct-path">
              <span className="acct-path-node owner">{ownerNames[best.ownerId] ?? best.ownerId}</span>
              <span className="acct-path-arrow" aria-hidden="true">→</span>
              {via && (
                <>
                  <button className="acct-path-node via" onClick={() => onOpen(via.person.personId)}>
                    <Avatar name={via.person.name} photoUrl={via.person.photoUrl} persona={via.person.persona} role={via.person.role} size={30} />
                    <span>{via.person.name}<small>{via.person.persona && via.person.persona !== 'other' ? via.person.persona : `${Math.round((via.person.relationshipStrength ?? 0) * 100)}% tie`}</small></span>
                  </button>
                  <span className="acct-path-arrow dashed" aria-hidden="true">⇢</span>
                </>
              )}
              <button className="acct-path-node target" onClick={() => onOpen(target.person.personId)}>
                <Avatar name={target.person.name} photoUrl={target.person.photoUrl} persona={target.person.persona} role={target.person.role} size={30} />
                <span>{target.person.name}<small>{target.person.title?.trim() || target.person.persona || 'contact'}</small></span>
              </button>
            </div>
            <p className="acct-why">{best.why}</p>
          </>
        ) : (
          <p className="acct-why muted">No warm tie into this account yet. {people.length > 0 ? `${people.length} people are known; none at 30% or better.` : 'No people are known here yet.'}</p>
        )}
        <div className="acct-flags">
          {inf.unreachedIds.length > 0 && <span className="acct-flag danger">{inf.unreachedIds.length} decision maker{inf.unreachedIds.length === 1 ? '' : 's'} with no path</span>}
          {quiet.length > 0 && <span className="acct-flag warn">{quiet.length} gone quiet</span>}
          {moved.length > 0 && <span className="acct-flag warn">{moved.length} may have moved</span>}
          {inf.bridges.length > 0 && <span className="acct-flag ok">{inf.bridges.length} bridge{inf.bridges.length === 1 ? '' : 's'} to other accounts</span>}
          {inf.unreachedIds.length === 0 && quiet.length === 0 && moved.length === 0 && inf.bridges.length === 0 && <span className="acct-flag">nothing flagged</span>}
        </div>
      </div>

      <div className="glance-stats acct-stats">
        <div className="glance-stat"><strong>{people.length}</strong><span>people known · {owners.size} of ours hold{owners.size === 1 ? 's' : ''} ties</span></div>
        <div className="glance-stat">
          <strong>{dmTotal === 0 ? '—' : `${dmReached} / ${dmTotal}`}</strong>
          <span>{dmTotal === 0 ? 'no decision maker identified' : `decision maker${dmTotal === 1 ? '' : 's'} reached`}</span>
        </div>
        <div className="glance-stat score-stat">
          {strongest != null ? (
            <span className="prof-score" style={{ ['--score' as string]: Math.max(0, Math.min(1, strongest / 100)) }}><strong>{strongest}</strong></span>
          ) : <strong>—</strong>}
          <span>strongest route</span>
        </div>
        <div className="glance-stat"><strong>{openPursuits}</strong><span>open pursuit{openPursuits === 1 ? '' : 's'} · {plan.engagements.length} on file</span></div>
        <div className="glance-stat"><strong>{pipeline > 0 ? formatMoney(pipeline) : '—'}</strong><span>pipeline named</span></div>
        <div className="glance-stat"><strong>{inf.routes.length}</strong><span>way{inf.routes.length === 1 ? '' : 's'} in · {inf.routes.filter((r) => r.viaPersonId).length} by introduction</span></div>
      </div>
    </section>
  )
}
